import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { useToast } from '../../contexts/ToastContext'
import IvideonPreview from './components/IvideonPreview'
import FullscreenPlayer from './components/FullscreenPlayer'
import { CameraManageModal, CameraAccessModal, PROVIDERS } from './components/CameraModals'
import './CamerasPage.css'

export default function CamerasPage({ onPageChange }) {
  const { profile } = useAuth()
  const toast = useToast()
  const isAdmin = profile && ['owner', 'admin', 'admin_employee'].includes(profile.role)
  const isOwner = profile?.role === 'owner'
  const pvzId = api.getActivePvzId()

  const [cameras, setCameras] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeFullscreen, setActiveFullscreen] = useState(null)

  // Ivideon config
  const [hasIvideon, setHasIvideon] = useState(false)
  const [yandexStatus, setYandexStatus] = useState(null)
  const [clips, setClips] = useState([])
  const [loadingClips, setLoadingClips] = useState(false)

  // Состояния для модального окна управления (ручная камера)
  const [showManageModal, setShowManageModal] = useState(false)
  const [editingCamera, setEditingCamera] = useState(null)
  const [formData, setFormData] = useState({ name: '', provider: 'other', embed_url: '', restrict_access: false, is_active: true })
  const [saving, setSaving] = useState(false)

  // Состояния для управления доступами
  const [showAccessModal, setShowAccessModal] = useState(false)
  const [accessCamera, setAccessCamera] = useState(null)
  const [grantedUserIds, setGrantedUserIds] = useState([])
  const [savingAccess, setSavingAccess] = useState(false)

  // Состояние загрузки каждого iframe отдельно
  const [iframeLoaded, setIframeLoaded] = useState({})

  // Реф для отслеживания перезагрузок
  const [reloadKey, setReloadKey] = useState(0)

  // Hover стрим — стрим включается только при наведении на карточку
  const [hoveredCameraId, setHoveredCameraId] = useState(null)

  useEffect(() => {
    if (!pvzId) return
    loadCameras()
    if (isAdmin) {
      loadEmployees()
      loadCloudStorage()
    }
    checkIvideon()
  }, [pvzId])

  const loadCloudStorage = async () => {
    try {
      const res = await api.getCloudStorageStatus()
      if (res?.success) setYandexStatus(res.status?.yandex)
      setLoadingClips(true)
      const list = await api.getAiCameraClips()
      setClips(list)
    } catch (err) {
      console.error('Cloud storage load error', err)
    } finally {
      setLoadingClips(false)
    }
  }

  const handleConnectYandex = async () => {
    try {
      const res = await api.getYandexDiskAuthUrl()
      if (res?.error) {
        toast.error(res.error)
      }
    } catch (err) {
      toast.error('Не удалось начать подключение к Яндекс.Диску')
    }
  }

  const checkIvideon = async () => {
    if (!isOwner) return
    try {
      const result = await api.getIvideonConfig()
      setHasIvideon(result.has_key)
    } catch (err) {
      // не критично
    }
  }

  const loadCameras = async () => {
    setLoading(true)
    try {
      let data = []
      if (isAdmin) {
        data = await api.getAllCamerasForAdmin(pvzId)
      } else {
        data = await api.getCameras(pvzId)
      }
      setCameras(data)
      // Сбрасываем состояния загрузки iframe
      const loadedStates = {}
      data.forEach(c => {
        loadedStates[c.id] = false
      })
      setIframeLoaded(loadedStates)
    } catch (err) {
      console.error('Cameras load error:', err)
      toast.error('Не удалось загрузить список камер')
    } finally {
      setLoading(false)
    }
  }

  const loadEmployees = async () => {
    try {
      const emps = await api.getEmployeesList(null, profile.org_id)
      setEmployees(emps.filter(e => e.is_approved !== false && !e.is_legacy))
    } catch (err) {
      console.error('Employees load error:', err)
    }
  }

  const handleIframeLoad = (id) => {
    setIframeLoaded(prev => ({ ...prev, [id]: true }))
  }

  const handleRefresh = () => {
    setReloadKey(prev => prev + 1)
    const resetLoaded = {}
    cameras.forEach(c => {
      resetLoaded[c.id] = false
    })
    setIframeLoaded(resetLoaded)
    toast.info('Обновление трансляций...')
  }

  // --- Управление камерами ---

  const openAddCamera = () => {
    setEditingCamera(null)
    setFormData({ name: '', provider: 'other', embed_url: '', restrict_access: false, is_active: true })
    setShowManageModal(true)
  }

  const openEditCamera = (camera) => {
    setEditingCamera(camera)
    setFormData({
      name: camera.name,
      provider: camera.provider || 'other',
      embed_url: camera.embed_url || '',
      restrict_access: camera.restrict_access || false,
      is_active: camera.is_active
    })
    setShowManageModal(true)
  }

  const handleSaveCamera = async () => {
    const isIvideonCamera = editingCamera?.ivideon_server_id
    
    if (!formData.name.trim()) {
      toast.warning('Заполните название камеры')
      return
    }
    
    // Для обычных камер обязательна embed-ссылка
    if (!isIvideonCamera && !formData.embed_url.trim()) {
      toast.warning('Заполните embed-ссылку')
      return
    }
    
    // Простейшая валидация URL
    if (!isIvideonCamera && formData.embed_url && !formData.embed_url.startsWith('http://') && !formData.embed_url.startsWith('https://')) {
      toast.warning('Ссылка должна начинаться с http:// или https://')
      return
    }

    setSaving(true)
    try {
      if (editingCamera) {
        await api.updateCamera(editingCamera.id, formData)
        toast.success('Настройки камеры сохранены')
      } else {
        await api.addCamera(pvzId, formData.name, formData.provider, formData.embed_url, formData.restrict_access)
        toast.success('Камера успешно добавлена')
      }
      setShowManageModal(false)
      loadCameras()
    } catch (err) {
      console.error('Save camera error:', err)
      toast.error('Не удалось сохранить настройки')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteCamera = async (cameraId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту камеру?')) return
    try {
      await api.deleteCamera(cameraId)
      toast.success('Камера удалена')
      loadCameras()
    } catch (err) {
      console.error('Delete camera error:', err)
      toast.error('Не удалось удалить камеру')
    }
  }

  // --- Управление доступами к конкретной камере ---

  const openAccessSettings = async (camera) => {
    setAccessCamera(camera)
    setGrantedUserIds([])
    setShowAccessModal(true)
    try {
      const accessList = await api.getCameraAccessList(camera.id)
      setGrantedUserIds(accessList.map(a => a.profile_id))
    } catch (err) {
      console.error('Load camera access error:', err)
      toast.error('Не удалось загрузить права доступа')
    }
  }

  const handleToggleAccess = async (employeeId) => {
    if (!accessCamera) return
    const hasAccess = grantedUserIds.includes(employeeId)
    setSavingAccess(true)
    try {
      if (hasAccess) {
        await api.revokeCameraAccess(accessCamera.id, employeeId)
        setGrantedUserIds(prev => prev.filter(id => id !== employeeId))
        toast.info('Доступ отозван')
      } else {
        await api.grantCameraAccess(accessCamera.id, employeeId, profile.id)
        setGrantedUserIds(prev => [...prev, employeeId])
        toast.success('Доступ предоставлен')
      }
    } catch (err) {
      console.error('Toggle camera access error:', err)
      toast.error('Не удалось обновить права доступа')
    } finally {
      setSavingAccess(false)
    }
  }

  // Рендер камеры
  const renderCameraStream = (camera) => {
    const isIvideon = camera.provider === 'ivideon' && camera.ivideon_server_id
    const embedUrl = camera.embed_url

    if (!camera.is_active) {
      return (
        <div className="camera-offline-overlay">
          <div className="offline-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg></div>
          <span>Камера отключена в настройках</span>
        </div>
      )
    }

    // Ivideon-камера — snapshot-грид (HLS только в плеере)
    if (isIvideon) {
      return (
        <IvideonPreview
          key={camera.id}
          camera={camera}
          hovered={hoveredCameraId === camera.id && !activeFullscreen}
          onOpenFullscreen={() => { setHoveredCameraId(null); setActiveFullscreen(camera) }}
        />
      )
    }

    // Обычная камера с embed_url
    if (embedUrl) {
      const isLoaded = iframeLoaded[camera.id]
      return (
        <>
          {!isLoaded && (
            <div className="camera-loader-overlay">
              <div className="spinner" />
              <span>Подключение к трансляции...</span>
            </div>
          )}
          <iframe
            key={`${camera.id}-${reloadKey}`}
            src={embedUrl}
            className="camera-iframe"
            allowFullScreen
            allow="autoplay; fullscreen"
            onLoad={() => handleIframeLoad(camera.id)}
          />
        </>
      )
    }

    // Нет embed_url и не Ivideon — пустая камера
    return (
      <div className="camera-offline-overlay">
        <div className="offline-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg></div>
        <span>Нет ссылки для трансляции</span>
      </div>
    )
  }

  return (
    <div className="cameras-page animate-fade-in">
      {/* Шапка страницы */}
      <div className="cameras-header">
        <div className="cameras-header-left">
          <div className="cameras-header-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg></div>
          <div>
            <div className="cameras-title">Видеонаблюдение</div>
            <div className="cameras-subtitle">Контроль и безопасность в режиме онлайн</div>
          </div>
        </div>
        <div className="cameras-header-actions">
          {cameras.length > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={handleRefresh}>
              Обновить
            </button>
          )}
          {isOwner && (
            <button 
              className="btn btn-secondary btn-sm" 
              onClick={() => onPageChange?.('settings', { tab: 'cameras' })}
            >
              Настройки Ivideon
            </button>
          )}
          {isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={openAddCamera}>
              Добавить камеру
            </button>
          )}
        </div>
      </div>

      {/* Лоадер или Сетка камер */}
      {loading ? (
        <div className="cameras-grid-skeleton">
          {[1, 2].map(i => (
            <div key={i} className="card camera-card-skeleton">
              <div className="skeleton camera-video-skeleton" />
              <div className="camera-info-skeleton">
                <div className="skeleton skeleton-title" style={{ width: '40%' }} />
                <div className="skeleton skeleton-subtitle" style={{ width: '25%' }} />
              </div>
            </div>
          ))}
        </div>
      ) : cameras.length === 0 ? (
        <div className="cameras-empty card">
          <div className="empty-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg></div>
          <h3>Камеры не настроены</h3>
          <p>
            {isOwner ? (
              <>
                Подключите камеры через <strong>Ivideon</strong> или добавьте вручную.
                {!hasIvideon && (
                  <span style={{ display: 'block', marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Создайте API ключ в личном кабинете Ivideon и вставьте его в настройках.
                  </span>
                )}
              </>
            ) : isAdmin ? (
              'Вы ещё не добавили ни одной камеры для этого ПВЗ.'
            ) : (
              'Для этого пункта выдачи камеры ещё не добавлены. Попросите владельца ПВЗ настроить их в системе.'
            )}
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {isOwner && !hasIvideon && (
              <button 
                className="btn btn-primary" 
                onClick={() => onPageChange?.('settings', { tab: 'cameras' })}
              >
                Подключить Ivideon
              </button>
            )}
            {isOwner && hasIvideon && (
              <button 
                className="btn btn-primary" 
                onClick={() => onPageChange?.('settings', { tab: 'cameras' })}
              >
                Выбрать камеры Ivideon
              </button>
            )}
            {isAdmin && (
              <button className="btn btn-secondary" onClick={openAddCamera}>
                Добавить камеру вручную
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="cameras-grid">
          {cameras.map(camera => (
            <div key={camera.id} className={`card camera-card ${!camera.is_active ? 'inactive' : ''}`}
              onMouseEnter={() => setHoveredCameraId(camera.id)}
              onMouseLeave={() => setHoveredCameraId(null)}
            >
              <div className="camera-video-container">
                {renderCameraStream(camera)}
                
                {camera.is_active && (camera.embed_url || (camera.provider === 'ivideon' && camera.ivideon_server_id)) && (
                  <button 
                    className="camera-fullscreen-btn" 
                    onClick={() => { setHoveredCameraId(null); setActiveFullscreen(camera); }}
                    title="На весь экран"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
                  </button>
                )}
              </div>

              <div className="camera-card-info">
                <div className="camera-meta">
                  <h4 className="camera-name">
                    {camera.name}
                    {camera.restrict_access && <span className="restrict-badge" title="Доступ ограничен"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>}
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className="camera-provider-badge">
                      {PROVIDERS[camera.provider] || PROVIDERS.other}
                    </span>
                  </div>
                </div>
                {isAdmin && (
                  <div className="camera-admin-actions">
                    {camera.restrict_access && (
                      <button 
                        className="btn-icon-edit" 
                        onClick={() => openAccessSettings(camera)}
                        title="Настроить доступы сотрудников"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      </button>
                    )}
                    <button 
                      className="btn-icon-edit" 
                      onClick={() => openEditCamera(camera)}
                      title="Редактировать"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button 
                      className="btn-icon-delete" 
                      onClick={() => handleDeleteCamera(camera.id)}
                      title="Удалить"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Интеграция Яндекс.Диск и Нарезанные ИИ-видеоклипы */}
      {isAdmin && (
        <div style={{ marginTop: '28px' }}>
          <div style={{
            background: 'var(--surface-color, #ffffff)',
            border: '1px solid var(--border-color, rgba(0, 0, 0, 0.08))',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: 'var(--text-primary)' }}>
                  ☁️ Яндекс.Диск и ИИ-видеоархив
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Автоматическое сохранение спорных моментов и операций на ваш облачный диск
                </p>
              </div>

              <div>
                {yandexStatus?.connected ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, background: 'rgba(34, 197, 94, 0.15)', color: '#16a34a', padding: '4px 10px', borderRadius: '12px' }}>
                      ✓ Яндекс.Диск подключён ({yandexStatus.email || 'Активен'})
                    </span>
                  </div>
                ) : (
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={handleConnectYandex}
                    style={{ background: '#fc3f1d', borderColor: '#fc3f1d', color: '#fff' }}
                  >
                    Подключить Яндекс.Диск
                  </button>
                )}
              </div>
            </div>

            {/* Список видеоклипов */}
            <div style={{ borderTop: '1px solid var(--border-color, #e2e8f0)', paddingTop: '16px' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
                Сохранённые видеоклипы ({clips.length})
              </h4>

              {loadingClips ? (
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '12px 0' }}>Загрузка фрагментов...</div>
              ) : clips.length === 0 ? (
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '12px 0' }}>
                  Видеоклипы ещё не нарезались. Они будут сохраняться автоматически при удержаниях и спорах.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                  {clips.map(c => (
                    <div key={c.id} style={{
                      padding: '10px 12px',
                      borderRadius: '10px',
                      background: 'var(--background-color, #f8fafc)',
                      border: '1px solid var(--border-color, #e2e8f0)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '11px', fontWeight: 800, color: 'var(--wb-primary)', textTransform: 'uppercase' }}>
                          {c.event_type || 'Запись'}
                        </span>
                        <span style={{ fontSize: '10.5px', color: 'var(--text-secondary)' }}>
                          {new Date(c.created_at).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{c.title}</span>
                      {c.yandex_disk_path && (
                        <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>
                          ☁️ На Яндекс.Диске: {c.yandex_disk_path}
                        </span>
                      )}
                      {c.video_url && (
                        <a href={c.video_url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: 'var(--wb-primary)', textDecoration: 'underline', marginTop: '4px' }}>
                          ▶ Просмотреть / Скачать видео
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Полноэкранный плеер */}
      {activeFullscreen && (
        <FullscreenPlayer
          camera={activeFullscreen}
          onClose={() => setActiveFullscreen(null)}
        />
      )}

      {/* Модалки */}
      <CameraManageModal
        isOpen={showManageModal}
        onClose={() => setShowManageModal(false)}
        camera={editingCamera}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSaveCamera}
        saving={saving}
      />

      <CameraAccessModal
        isOpen={showAccessModal}
        onClose={() => setShowAccessModal(false)}
        camera={accessCamera}
        employees={employees}
        grantedUserIds={grantedUserIds}
        onToggleAccess={handleToggleAccess}
        savingAccess={savingAccess}
      />
    </div>
  )
}
